import React, { useCallback, useState, useEffect } from 'react';
import { GridRowParams } from '@material-ui/data-grid';
import FormDefault from '~/components/FormDefault';
import Search from '~/components/Search';
import { Col, Row } from 'react-bootstrap';
import { Form } from './components/Header/Form';
import { FormProduto } from './components/Body/FormProduto';
import { Footer } from './components/Footer/index';
import { transformAsCurrency, formatCurrencyAsText } from '~/utils/functions';
import api from '~/services/api';
import { initialFormData } from './context/CadastroComposicaoContext';
import { useCadastroComposicao } from './hook/useCadastroComposicao';
import { Container, FormComposicao } from './styles';
import Separator from '~/components/Separator';
import { toast } from 'react-toastify';
import { nanoid } from 'nanoid';
import { insertOrUpdate } from '~/utils/masterDetail';
import { calcularTotais } from './functions/calcularTotais';
import { useHandleFocus } from './hook/useFocus';
import DefaultLoader from '~/components/DefaultLoader';

const CadastroComposicaoContent: React.FC = () => {
  const masterDetailDefault = [
    {
      obj_name: 'produtos',
      pk_fields: ['cod_seq_produto_composicao'],
      itens: {
        insert: [],
        update: [],
        delete: [],
      },
    },
  ];
  const [loader, setLoader] = useState<boolean>(false);
  const [showSearch, setShowSearch] = useState(true);

  const {
    formData,
    setFormData,
    isUpdate,
    setUpdate,
    tipoPrecoComposicao,
    setTipoPrecoComposicao,
    masterDetail,
    setMasterDetail,
    rows,
    setRows,
    reset,
    getValues,
    setValue,
    setUpdateItems,
    setValorTotalLoja,
    setValorTotalComposicao,
    setCodItemEmbalagem,
    codItemEmbalagem,
    setValPreco,
    containerRef,
    setExplodeVenda,
  } = useCadastroComposicao();
  const { handleInputFocus } = useHandleFocus();

  const resetFormData = useCallback(() => {
    setFormData(initialFormData);
    setTipoPrecoComposicao({
      label: 'Acompanha a soma dos itens',
      value: 0,
    });
    setUpdateItems(false);
    setUpdate(false);
    setRows([]);
    setValorTotalLoja(0);
    setValPreco(0);
    setValorTotalComposicao(0);
    setMasterDetail(masterDetailDefault);
    reset();
  }, [
    setFormData,
    setMasterDetail,
    masterDetailDefault,
    setValorTotalLoja,
    setValorTotalComposicao,
    setRows,
    setUpdate,
    setUpdateItems,
    setTipoPrecoComposicao,
    setValPreco,
  ]);

  useEffect(() => {
    if (showSearch) return;
    const { val_preco } = getValues();
    if (!val_preco) {
      return handleInputFocus('val_preco');
    }
    if (val_preco) {
      return handleInputFocus('cod_produto');
    }
  }, [handleInputFocus, showSearch, getValues]);

  useEffect(() => {
    const { totalLoja, totalComposicao } = calcularTotais(
      rows.map((item) => ({
        val_venda: item.val_venda ?? 0,
        val_venda_composicao: item.val_venda_composicao ?? 0,
        qtd_composicao: item.qtd_composicao ?? 0,
      })),
    );
    const { val_preco } = getValues();
    setValue('qtd_composicao', '');
    setValue('cod_produto', '');
    setUpdateItems(false);
    switch (tipoPrecoComposicao.value) {
      case 1: {
        setValue('val_preco', val_preco);
        setValue('val_venda_composicao', formatCurrencyAsText(0));
        break;
      }
      case 2: {
        setValue('val_preco', formatCurrencyAsText(totalComposicao));
        setValorTotalComposicao(totalComposicao);
        break;
      }
      default: {
        setValue('val_preco', formatCurrencyAsText(totalLoja));
        setValue('val_venda_composicao', formatCurrencyAsText(0));
        setValorTotalComposicao(totalLoja);
        break;
      }
    }
  }, [tipoPrecoComposicao.value, rows]);

  const getComposicao = async (
    cod_loja: number,
    cod_produto: number,
    tipo_preco_composicao: number,
    val_venda: number,
  ) => {
    const { data } = await api.get(
      `/cadastro-de-composicao/${cod_loja}/${cod_produto}`,
    );
    if (data.data && data.data.length > 0) {
      const composicaoRows = Array.from(data.data);
      const filteredRows = composicaoRows.filter(
        (item: any) =>
          !rows.some(
            (existing: any) =>
              existing.cod_loja === cod_loja &&
              existing.cod_produto_composicao === item.cod_produto_composicao,
          ),
      );

      const options = filteredRows.map((item: any) => {
        const uuid = nanoid();
        return {
          uuid,
          id: uuid,
          cod_loja: Number(cod_loja),
          cod_produto: item.cod_produto_composicao,
          cod_produto_composicao: item.cod_produto_composicao,
          des_produto: item.des_produto,
          val_venda_composicao: transformAsCurrency(item.val_venda_composicao),
          val_custo_rep: transformAsCurrency(item.val_custo_rep),
          val_venda: transformAsCurrency(item.val_venda),
          qtd_composicao: transformAsCurrency(item.qtd_composicao),
          qtd_est_atual: item.qtd_estoque,
          flg_ipv: item.flg_ipv,
        };
      }) as any;

      const promises = options.map((row: any) =>
        insertOrUpdate('produtos', row, masterDetail, setMasterDetail),
      );

      const results = await Promise.all(promises);
      const rowsDetail = results.flat();

      const uniqueRows = Array.from(
        new Map(
          rowsDetail.map((item) => [`${item.id}_${item.cod_produto}`, item]),
        ).values(),
      );

      const { totalLoja, totalComposicao } = calcularTotais(uniqueRows);

      setValorTotalLoja(totalLoja);
      setValorTotalComposicao(totalComposicao);

      setRows(uniqueRows);

      switch (tipo_preco_composicao) {
        case 1: {
          setValue('val_preco', formatCurrencyAsText(val_venda));
          setValorTotalComposicao(val_venda);
          break;
        }
        case 2: {
          setValue('val_preco', formatCurrencyAsText(totalComposicao));
          break;
        }
        default: {
          setValue('val_preco', formatCurrencyAsText(totalLoja));
          break;
        }
      }
      setLoader(false);
    } else {
      setValorTotalLoja(0);
      setValorTotalComposicao(0);
      setValPreco(0);
      setRows([]);
      setLoader(false);
    }
  };

  const onRowClick = async (param: GridRowParams) => {
    const { row } = param;
    const {
      cod_loja,
      cod_produto,
      des_produto,
      tipo_preco_composicao,
      qtd_decomp,
      val_venda,
      flg_explosao_venda,
    } = row;

    getComposicao(cod_loja, cod_produto, tipo_preco_composicao, val_venda);
    setCodItemEmbalagem(row.cod_item_embalagem);
    setFormData({
      ...formData,
      cod_loja: {
        value: cod_loja,
        isInvalid: false,
        isRequired: false,
      },
      cod_produto: {
        value: cod_produto,
        isInvalid: false,
        isRequired: false,
      },
      des_produto: {
        value: des_produto,
        isInvalid: false,
        isRequired: false,
      },
      tipo_preco_composicao: {
        value: tipo_preco_composicao,
        isInvalid: false,
        isRequired: false,
      },
      qtd_composicao: {
        value: qtd_decomp,
        isInvalid: false,
        isRequired: false,
      },
    });
    setExplodeVenda(flg_explosao_venda);
    setUpdate(true);
    setLoader(false);
    setShowSearch(false);
    switch (tipo_preco_composicao) {
      case 0: {
        setTipoPrecoComposicao({
          label: 'Acompanha a soma dos itens',
          value: 0,
        });
        setValue('tipo_preco_composicao', {
          label: 'Acompanha a soma dos itens',
          value: 0,
        });
        return;
      }
      case 1: {
        setTipoPrecoComposicao({
          label: 'Informarei o preço da composição',
          value: 1,
        });
        setValue('tipo_preco_composicao', {
          label: 'Informarei o preço da composição',
          value: 1,
        });
        return;
      }
      default: {
        setTipoPrecoComposicao({
          label: 'Informarei o preço de cada item',
          value: 2,
        });
        setValue('tipo_preco_composicao', {
          label: 'Informarei o preço de cada item',
          value: 2,
        });
      }
    }
  };

  const onSave = useCallback(async () => {
    if (rows.length <= 0) {
      setLoader(false);
      handleInputFocus('busca_produto');
      return toast.warn('Ao menos um produto deve ser informado');
    }

    const { val_preco } = getValues();
    const composicaoData = {
      cod_loja: formData.cod_loja.value,
      cod_produto: formData.cod_produto.value,
      cod_item_embalagem: codItemEmbalagem,
      tipo_preco_composicao: tipoPrecoComposicao.value,
      val_composicao: transformAsCurrency(val_preco),
      masterDetail,
    };

    if (
      tipoPrecoComposicao.value === 1 &&
      transformAsCurrency(val_preco) <= 0
    ) {
      setLoader(false);
      handleInputFocus('val_preco');
      return toast.warn('Preço de Venda deve ser informado.');
    }

    const { data } = await api.post('/cadastro-de-composicao', {
      composicaoData,
    });
    if (data.success) {
      toast.success(data.message);
      setShowSearch(true);
      resetFormData();
      setUpdateItems(false);
    }

    setLoader(false);
  }, [
    formData.cod_loja.value,
    rows,
    formData.cod_produto.value,
    resetFormData,
    getValues,
    codItemEmbalagem,
    handleInputFocus,
    masterDetail,
    setUpdateItems,
    tipoPrecoComposicao.value,
  ]);

  if (loader) {
    return <DefaultLoader />;
  }

  return (
    <Container>
      {showSearch && (
        <Search
          onlyReport
          newData={() => null}
          onRowClick={onRowClick}
          codTela={312}
        />
      )}

      {!showSearch && (
        <FormDefault
          codTela={312}
          codigoRegistro={[
            {
              des_campo: 'Código',
              value: formData.cod_produto.value,
            },
          ]}
          title="Cadastro de Composição"
          onSave={async () => {
            await onSave();
          }}
          onCancel={() => null}
          isUpdate={isUpdate}
          onNew={() => null}
          onDelete={() => null}
          isDelete={false}
          isNew={false}
          onClearFields={() => null}
          onReturnSearch={() => {
            setShowSearch(true);
            resetFormData();
          }}
        >
          <FormComposicao ref={containerRef}>
            <Row>
              <Col sm={12} className="mt-4">
                <Form />
              </Col>
            </Row>
            <Col md={12} sm={12}>
              <Separator
                labelText="Produtos da Composição"
                sidePadding="0 0"
                marginTop="5px"
              />
            </Col>
            <Row>
              <Col sm={12}>
                <FormProduto />
              </Col>
            </Row>
            <Row>
              <Col sm={12}>
                <Footer />
              </Col>
            </Row>
          </FormComposicao>
        </FormDefault>
      )}
    </Container>
  );
};

export default CadastroComposicaoContent;
